/**
 * Merchant billing API adapter (REQ-20260716-02 / REQ-20260724-01).
 * Aligned to custom/config/initializers/billing_routes.rb + controllers.
 *
 * GET  /api/v1/accounts/:id/subscription
 *   → { subscription, wallet, credits, catalog }
 * GET  /api/v1/accounts/:id/wallet
 *   → { balance_cents, currency, ledger: [...] }
 * POST /api/v1/accounts/:id/wallet_topups
 *   body: { amount_cents }
 * GET  /api/v1/accounts/:id/billing_cash_payments
 * GET  /api/v1/accounts/:id/billing_cash_payments/:id
 * GET  /api/v1/accounts/:id/credit_balance
 *   → { available_credits, debt_credits, buckets: [...] }
 * GET  /api/v1/accounts/:id/billing_orders
 *   → { payload: [...] }
 * POST /api/v1/accounts/:id/billing_orders
 *   body: { order_type, seat_limit?, pack_count?, idempotency_key? }
 *   order_type: plan_upgrade | plan_renew | seats | ai_credits
 * Errors: 403; 422 { error: 'insufficient_wallet_balance'|'invalid_purchase'|'wallet_spend_frozen'|..., message }
 */
const http = () => window.axios;

const accountBase = accountId => `/api/v1/accounts/${accountId}`;

export const ORDER_TYPES = Object.freeze({
  PLAN_UPGRADE: 'plan_upgrade',
  PLAN_RENEW: 'plan_renew',
  SEATS: 'seats',
  AI_CREDITS: 'ai_credits',
});

/** @deprecated use ORDER_TYPES */
export const PURCHASE_TYPES = ORDER_TYPES;

export const TOPUP_TIERS_CENTS = Object.freeze([2000, 5000, 10000, 20000]);

export const fetchSubscriptionOverview = async accountId => {
  const { data } = await http().get(`${accountBase(accountId)}/subscription`);
  return data;
};

export const fetchWallet = async accountId => {
  const { data } = await http().get(`${accountBase(accountId)}/wallet`);
  return data;
};

export const createWalletTopup = async (accountId, amountCents) => {
  const { data } = await http().post(`${accountBase(accountId)}/wallet_topups`, {
    amount_cents: amountCents,
  });
  return data;
};

export const fetchBillingCashPayments = async accountId => {
  const { data } = await http().get(
    `${accountBase(accountId)}/billing_cash_payments`
  );
  return data?.payload || [];
};

export const fetchBillingCashPayment = async (accountId, paymentId) => {
  const { data } = await http().get(
    `${accountBase(accountId)}/billing_cash_payments/${paymentId}`
  );
  return data;
};

export const fetchCreditBalance = async accountId => {
  const { data } = await http().get(`${accountBase(accountId)}/credit_balance`);
  return data;
};

export const fetchCreditLedger = async (accountId, params = {}) => {
  const { data } = await http().get(
    `${accountBase(accountId)}/credit_ledger_entries`,
    { params }
  );
  return data?.payload || [];
};

export const fetchBillingOrders = async accountId => {
  const { data } = await http().get(`${accountBase(accountId)}/billing_orders`);
  return data;
};

export const createBillingOrder = async (accountId, payload) => {
  const { data } = await http().post(
    `${accountBase(accountId)}/billing_orders`,
    payload
  );
  return data;
};

export const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `billing-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Normalize subscription show payload into UI-friendly shape.
 */
export const normalizeOverview = data => {
  const subscription = data?.subscription || null;
  const catalog = data?.catalog || null;
  const wallet = data?.wallet || null;
  const credits = data?.credits || null;

  const seats = subscription
    ? {
        used: subscription.seats_used,
        limit: subscription.seat_limit,
        pending_limit: subscription.pending_seat_limit,
        base_seats: catalog?.base_seats ?? 5,
        cap: catalog?.seat_cap ?? 100,
      }
    : null;

  return {
    subscription,
    seats,
    wallet,
    credits,
    catalog,
  };
};
